/** Always 30 tick positions; ~3 tick gaps before min length (2–3 when possible; fewer if axis clamps at 0). English and metric use the same rule. */
const LENGTH_TICK_COUNT = 30;
const LENGTH_GAP_COUNT = LENGTH_TICK_COUNT - 1;
/** Tick intervals to leave before the grid cell containing xMin (target 3; use 2 if xMin is too small for 3). */
const LENGTH_PAD_TICKS_BEFORE_MIN = 3;
const LENGTH_PAD_TICKS_BEFORE_MIN_FALLBACK = 2;

/** Nice English steps only: 0.1, 0.25, 0.5, 1, 5, 10, 100 (client). Unwanted kept commented. */
const ENGLISH_STEP_LADDER = [
  0.1,
  // 0.2, 0.3, 0.4,
  0.25,
  0.5,
  // 0.6, 0.7, 0.8, 0.9,
  1,
  // 1.5, 2, 2.5,
  5,
  10,
  // 20, 50,
  100,
];

const METRIC_STEP_LADDER = [
  2, 5, 10,
  // 15,
  20, 25,
  // 30, 35, 40, 45,
  50,
  // 60, 70, 80, 90,
  100, 150, 200, 250,
  // 300, 400,
  500,
  // 750,
  1000, 1500, 2000,
];

/**
 * Base step from data span (max length − min length). Finer gaps for smaller spans.
 * If (LENGTH_TICK_COUNT - 1) * step < span, the caller bumps step until the window fits.
 * English steps restricted to 0.1 / 0.25 / 0.5 / 1 / 5 / 10 / 100.
 */
function englishStepForSpan(span) {
  // if (span < 1) return 0.05;
  // if (span < 2) return 0.05;
  if (span < 4) return 0.1;
  // if (span < 8) return 0.2;
  // if (span < 12) return 0.3;
  if (span < 12) return 0.25;
  // if (span < 16) return 0.4;
  if (span < 20) return 0.5;
  // if (span < 24) return 0.6;
  // if (span < 28) return 0.7;
  // if (span < 32) return 0.8;
  // if (span < 36) return 0.9;
  if (span < 40) return 1;
  // return 1.2;
  if (span < 100) return 5;
  if (span < 200) return 10;
  return 100;
}

function metricStepForSpan(span) {
  if (span < 50) return 2;
  if (span < 100) return 5;
  if (span < 200) return 10;
  if (span < 400) return 15;
  if (span < 600) return 20;
  if (span < 800) return 25;
  if (span < 1000) return 30;
  if (span < 1200) return 35;
  if (span < 1400) return 40;
  if (span < 1600) return 45;
  if (span < 1800) return 50;
  if (span < 2000) return 55;
  if (span < 2200) return 60;
  if (span < 2400) return 65;
  if (span < 2600) return 70;
  if (span < 2800) return 75;
  if (span < 3000) return 80;
  if (span < 3200) return 85;
  if (span < 3400) return 90;
  if (span < 3600) return 95;
  if (span < 4000) return 100;
  return 200;
}

function nextLargerStep(step, ladder) {
  const i = ladder.findIndex((s) => s > step + 1e-12);
  return i >= 0 ? ladder[i] : step * 2;
}

function startForStep(xMin, step, padTicks) {
  const gridBelow = Math.floor(xMin / step + 1e-9) * step;
  return Math.max(0, gridBelow - padTicks * step);
}

function buildSymmetricLengthTicks(xMin, xMax, unitValue) {
  const isEnglish = unitValue === "English";
  const ladder = isEnglish ? ENGLISH_STEP_LADDER : METRIC_STEP_LADDER;
  const decimals = isEnglish ? 2 : 0;

  if (!Number.isFinite(xMin) || !Number.isFinite(xMax)) {
    const s = isEnglish ? 0.1 : 10;
    return Array.from({ length: LENGTH_TICK_COUNT }, (_, i) =>
      Number((i * s).toFixed(decimals))
    );
  }

  const span = Math.max(xMax - xMin, 1e-12);
  let step = isEnglish ? englishStepForSpan(span) : metricStepForSpan(span);
  while (LENGTH_GAP_COUNT * step < span - 1e-9) {
    step = nextLargerStep(step, ladder);
  }

  function tryPad(padTicks) {
    let s = step;
    while (LENGTH_GAP_COUNT * s < span - 1e-9) {
      s = nextLargerStep(s, ladder);
    }
    let start = startForStep(xMin, s, padTicks);
    let end = start + LENGTH_GAP_COUNT * s;
    let guard = 0;
    while (end < xMax - 1e-9 && guard < 64) {
      guard += 1;
      s = nextLargerStep(s, ladder);
      while (LENGTH_GAP_COUNT * s < span - 1e-9) {
        s = nextLargerStep(s, ladder);
      }
      start = startForStep(xMin, s, padTicks);
      end = start + LENGTH_GAP_COUNT * s;
    }
    return { start, step: s };
  }

  let { start, step: finalStep } = tryPad(LENGTH_PAD_TICKS_BEFORE_MIN);
  if (start + (LENGTH_PAD_TICKS_BEFORE_MIN - 1) * finalStep > xMin + 1e-9) {
    ({ start, step: finalStep } = tryPad(LENGTH_PAD_TICKS_BEFORE_MIN_FALLBACK));
  }

  return Array.from({ length: LENGTH_TICK_COUNT }, (_, i) =>
    Number((start + i * finalStep).toFixed(decimals))
  );
}

export function buildLengthTicks(values, unitValue) {
  const nums = (values || []).map(Number).filter(Number.isFinite);
  if (!nums.length) {
    return buildSymmetricLengthTicks(0, 0, unitValue);
  }
  const xMin = Math.min(...nums);
  const xMax = Math.max(...nums);
  return buildSymmetricLengthTicks(xMin, xMax, unitValue);
}

export function buildWeightTicks(values, unitValue, targetTickCount = 20) {
  const yMax = values.length ? Math.max(...values) : 0;
  const isEnglish = unitValue === "English";
  let step = isEnglish ? 0.2 : 50;

  if (isEnglish) {
    // Nice steps only: 0.1, 0.25, 0.5, 1, 5, 10, 100 (unwanted kept commented).
    // if (yMax < 1.5) step = 0.05;
    if (yMax < 2) step = 0.1;
    // else if (yMax < 4) step = 0.2;
    else if (yMax < 4) step = 0.25;
    // else if (yMax < 8) step = 0.4;
    else if (yMax < 8) step = 0.5;
    // else if (yMax < 12) step = 0.6;
    // else if (yMax < 16) step = 0.8;
    else if (yMax < 20) step = 1.0;
    // else if (yMax < 24) step = 1.2;
    // else if (yMax < 28) step = 1.4;
    // else if (yMax < 32) step = 1.6;
    // else if (yMax < 36) step = 1.8;
    // else step = 2.0;
    else if (yMax < 50) step = 5;
    else if (yMax < 100) step = 10;
    else step = 100;
  } else {
    if (yMax < 200) step = 10;
    else if (yMax < 400) step = 20;
    // else if (yMax < 600) step = 30;
    // else if (yMax < 800) step = 40;
    else if (yMax < 1000) step = 50;
    // else if (yMax < 1200) step = 60;
    // else if (yMax < 1400) step = 70;
    // else if (yMax < 1600) step = 80;
    // else if (yMax < 1800) step = 90;
    else if (yMax < 2000) step = 100;
    // else if (yMax < 2200) step = 110;
    // else if (yMax < 2400) step = 120;
    // else if (yMax < 2600) step = 130;
    // else if (yMax < 2800) step = 140;
    // else if (yMax < 3000) step = 150;
    // else if (yMax < 3200) step = 160;
    // else if (yMax < 3400) step = 170;
    // else if (yMax < 3600) step = 180;
    // else if (yMax < 3800) step = 190;
    else step = 200;
  }

  const requiredRange = (targetTickCount - 1) * step;
  const upper = Math.ceil(Math.max(yMax, requiredRange) / step) * step;
  const ticks = [];
  for (let v = 0; v <= upper + 1e-9; v += step) {
    ticks.push(Number(v.toFixed(isEnglish ? 2 : 0)));
  }
  while (ticks.length < targetTickCount) {
    const last = ticks[ticks.length - 1];
    ticks.push(Number((last + step).toFixed(isEnglish ? 2 : 0)));
  }
  return ticks;
}

/**
 * Fall-back Wr axis band when there is no data (≈ client optional fixed 70%–130%).
 * Values are Wr ratios (1 = 100%).
 */
export const WR_AXIS_RATIO_FALLBACK_MIN = 0.7;
export const WR_AXIS_RATIO_FALLBACK_MAX = 1.3;

/**
 * Extra band on Wr axis: 10 percentage points below min and 10 above max (ratio: 0.1 = 10%).
 * Example: data peak at 120% Wr (1.2) → y-axis max 130% (1.3); floor at 87% (0.87) → 77% (0.77).
 */
const WR_AXIS_PAD_POINTS = 0.1;

/**
 * Y-axis domain for Wr (ratio).
 * Pass every relevant sample (points and optional ± error bands pre-expanded into `values`).
 * @param {number[]} values - Wr ratios to derive extent from
 * @returns {{ yMin: number, yMax: number }}
 */
export function computeWrAxisDomainFromData(values) {
  const nums = (values || []).map(Number).filter((v) => Number.isFinite(v));
  if (!nums.length) {
    return { yMin: WR_AXIS_RATIO_FALLBACK_MIN, yMax: WR_AXIS_RATIO_FALLBACK_MAX };
  }
  let dMin = Math.min(...nums);
  let dMax = Math.max(...nums);
  const pad = WR_AXIS_PAD_POINTS;
  if (Math.abs(dMax - dMin) < 1e-12) {
    const v = dMin;
    return {
      yMin: v - pad,
      yMax: v + pad,
    };
  }
  return {
    yMin: dMin - pad,
    yMax: dMax + pad,
  };
}

/**
 * Tick positions for a Wr y-axis over [yMin, yMax] (ratio).
 */
export function buildWrYTicksForDomain(yMin, yMax) {
  if (!Number.isFinite(yMin) || !Number.isFinite(yMax) || yMax <= yMin) {
    const step = 0.1;
    const out = [];
    for (
      let v = WR_AXIS_RATIO_FALLBACK_MIN;
      v <= WR_AXIS_RATIO_FALLBACK_MAX + 1e-9;
      v += step
    ) {
      out.push(Number(v.toFixed(4)));
    }
    return out;
  }
  const span = yMax - yMin;
  const niceSteps = [0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1.0];
  let roughStep = span / 9;
  let step = niceSteps.find((s) => s >= roughStep * 0.85) ?? 1;
  let start = Math.floor((yMin - 1e-9) / step) * step;
  let end = Math.ceil((yMax + 1e-9) / step) * step;
  const build = () => {
    const ticks = [];
    for (let v = start; v <= end + 1e-9; v += step) {
      ticks.push(Number(Number(v).toFixed(4)));
    }
    return ticks;
  };
  let ticks = build();
  let guard = 0;
  while (ticks.length > 14 && guard < 12) {
    guard += 1;
    const idx = niceSteps.indexOf(step);
    step = idx >= 0 && idx < niceSteps.length - 1 ? niceSteps[idx + 1] : step * 2;
    start = Math.floor((yMin - 1e-9) / step) * step;
    end = Math.ceil((yMax + 1e-9) / step) * step;
    ticks = build();
  }
  if (!ticks.some((t) => Math.abs(t - 1) < 1e-5) && yMin <= 1 && yMax >= 1) {
    ticks.push(1);
    ticks = [...new Set(ticks)].sort((a, b) => a - b);
  }
  return ticks.length ? ticks : [yMin, yMax];
}

/**
 * @returns {{ yMin: number, yMax: number, yTicks: number[] }}
 */
export function getWrYAxisFromValues(values) {
  const { yMin, yMax } = computeWrAxisDomainFromData(values);
  const yTicks = buildWrYTicksForDomain(yMin, yMax);
  return { yMin, yMax, yTicks };
}

/**
 * Y-axis ticks for Relative Weight (Wr) — matches legacy WrVsLength / AvgWrVsDate Recharts logic.
 */
export function buildWrYTicks(values) {
  const yValues = (values || []).map((v) => Number(v || 0));
  const yMaxVal = yValues.length ? Math.max(...yValues) : 0;
  let yStep = 0.2;
  const yCandidateUpper = yMaxVal + 0.1;
  let estimatedTickCount = Math.ceil(yCandidateUpper / yStep) + 1;
  if (estimatedTickCount > 12) {
    yStep = 0.4;
  }
  const minTicks = 10;
  const minUpperForMinTicks = (minTicks - 1) * yStep;
  let yUpper = Math.max(yCandidateUpper, minUpperForMinTicks);
  yUpper = Math.ceil(yUpper / yStep) * yStep;
  const yTicks = [];
  for (let v = 0; v <= yUpper + 1e-9; v += yStep) {
    yTicks.push(Number(v.toFixed(1)));
  }
  if (!yTicks.includes(1)) {
    yTicks.push(1);
  }
  return Array.from(new Set(yTicks)).sort((a, b) => a - b);
}

/**
 * Dual Y for Number Frequency — left fish counts, right Avg Wr (0.2 steps), aligned tick counts.
 */
export function buildNumberFrequencyDualYTicks(leftMaxNumObserved, rightMaxAvgObserved) {
  const baseTickCount = 10;
  const wrStep = 0.2;
  const neededRightTicks =
    Math.floor(Math.ceil((Number(rightMaxAvgObserved) + 1e-9) / wrStep)) + 1;
  const targetTickCount = Math.max(baseTickCount, neededRightTicks);
  const leftSegments = targetTickCount - 1;
  const fishStep = Math.max(1, Math.ceil(Number(leftMaxNumObserved) / leftSegments));
  const leftUpper = fishStep * leftSegments;
  const leftTicksNum = [];
  for (let v = 0; v <= leftUpper; v += fishStep) {
    leftTicksNum.push(v);
  }
  const rightUpper = wrStep * leftSegments;
  const rightTicksAvg = [];
  for (let v = 0; v <= rightUpper + 1e-9; v += wrStep) {
    rightTicksAvg.push(Number(v.toFixed(2)));
  }
  if (!rightTicksAvg.includes(1)) {
    rightTicksAvg.push(1);
  }
  rightTicksAvg.sort((a, b) => a - b);
  const y2Max = Math.max(rightUpper, ...rightTicksAvg);
  return { leftTicks: leftTicksNum, rightTicks: rightTicksAvg, leftUpper, y2Max };
}

/** Number Frequency dual Y: tiered gaps; tick count follows data max. */
const NF_WR_RATIO_MIN = 0.7;
/** Right Avg Wr axis always reaches at least 110% (Wr = 100% line stays visible). */
const NF_WR_RATIO_MAX_FLOOR = 1.1;
const NF_FISH_GAP_LOW_THRESHOLD = 70;
const NF_FISH_GAP_HIGH_THRESHOLD = 140;
const NF_WR_PCT_THRESHOLD = 135;

function fishGapForMax(leftMax) {
  // Tiered gaps: finer steps under 70 (1 / 2 / 3 / 4 / 5), then 10 / 20 / … for larger bars
  // so Plotly does not paint hundreds of overlapping Y labels.
  if (leftMax < 14) return 1;
  if (leftMax < 28) return 2;
  if (leftMax < 42) return 3;
  if (leftMax < 56) return 4;
  if (leftMax < NF_FISH_GAP_LOW_THRESHOLD) return 5;
  if (leftMax <= NF_FISH_GAP_HIGH_THRESHOLD) return 10;
  if (leftMax <= 280) return 20;
  if (leftMax <= 700) return 50;
  if (leftMax <= 1400) return 100;
  if (leftMax <= 2800) return 200;
  if (leftMax <= 7000) return 500;
  if (leftMax <= 14000) return 1000;
  if (leftMax <= 28000) return 2000;
  return 5000;
}

/**
 * Number Frequency dual Y — left fish count (gap 1 / 2 / 3 / 4 / 5 / 10 / 20 / …),
 * right Avg Wr from 70% to at least 110% (gap 5% or 10%). Equal tick counts both sides.
 * Pass all Wr samples including ±stdev; right axis still reaches 110% with no Wr data.
 * Left axis scales with observed stack totals so tall bins are not clipped.
 */
export function buildNumberFrequencyDualYTicksDataBound(
  leftMaxNumObserved,
  wrRatioSamples
) {
  const leftMax = Math.max(0, Number(leftMaxNumObserved) || 0);
  const fishGap = fishGapForMax(leftMax);
  let leftUpper = Math.max(
    fishGap,
    Math.ceil((leftMax + fishGap) / fishGap) * fishGap
  );

  const nums = (wrRatioSamples || []).map(Number).filter(Number.isFinite);
  let dataMaxRatio;
  if (!nums.length) {
    // No Wr samples: still show full standard band through 110%.
    dataMaxRatio = NF_WR_RATIO_MAX_FLOOR;
  } else {
    const dMax = Math.max(...nums);
    dataMaxRatio = Math.max(dMax + WR_AXIS_PAD_POINTS, NF_WR_RATIO_MAX_FLOOR);
  }
  const dataMaxPct = dataMaxRatio * 100;
  const wrGapPct = dataMaxPct > NF_WR_PCT_THRESHOLD ? 10 : 5;
  const wrGap = wrGapPct / 100;
  const wrYMin = NF_WR_RATIO_MIN;
  const wrNeedMax = Math.max(NF_WR_RATIO_MAX_FLOOR, dataMaxRatio);

  // Right ticks first: 70% … at least 110% (or higher if data needs it).
  const rightTicks = [];
  for (let v = wrYMin; v <= wrNeedMax + 1e-9; v += wrGap) {
    rightTicks.push(Number(v.toFixed(4)));
  }
  // Guarantee last tick is at least the floor when float noise stops short.
  if (
    !rightTicks.length ||
    rightTicks[rightTicks.length - 1] < NF_WR_RATIO_MAX_FLOOR - 1e-9
  ) {
    rightTicks.push(NF_WR_RATIO_MAX_FLOOR);
  }

  // Natural left ticks from fish-gap scaling.
  let leftTicks = [];
  for (let v = 0; v <= leftUpper + 1e-9; v += fishGap) {
    leftTicks.push(v);
  }

  // Equal tick counts: extend the shorter side (keep each side's step size).
  const targetCount = Math.max(leftTicks.length, rightTicks.length);
  while (leftTicks.length < targetCount) {
    leftUpper += fishGap;
    leftTicks.push(leftUpper);
  }
  while (rightTicks.length < targetCount) {
    const last = rightTicks[rightTicks.length - 1];
    rightTicks.push(Number((last + wrGap).toFixed(4)));
  }

  const wrYMax = rightTicks[rightTicks.length - 1];

  return {
    leftTicks,
    rightTicks,
    leftUpper,
    wrYMin,
    wrYMax,
  };
}

/**
 * Y ticks for "percentage of population" bar charts (0–100+ scale).
 * Matches legacy upper bound `max(10, ceil(maxPct*2)/2)` but caps tick count so Plotly
 * does not draw hundreds of grid lines when maxPct is large or data is malformed.
 */
export function buildPopulationPctYTicks(maxPctObserved, maxTickCount = 12) {
  const maxPct = Number.isFinite(Number(maxPctObserved))
    ? Math.max(0, Number(maxPctObserved))
    : 0;
  let upper = Math.max(10, Math.ceil(maxPct * 2) / 2);

  const tickCountUnitStep = Math.floor(upper) + 1;
  if (tickCountUnitStep <= maxTickCount) {
    const ticks = [];
    for (let v = 0; v <= upper + 1e-9; v += 1) {
      ticks.push(Number(v.toFixed(1)));
    }
    const rangeMax = ticks[ticks.length - 1] ?? upper;
    return { ticks, rangeMax };
  }

  const targetIntervals = maxTickCount - 1;
  const roughStep = upper / targetIntervals;
  let step = Math.max(1, Math.ceil(roughStep));
  if (step <= 2) {
    step = 2;
  } else if (step <= 5) {
    step = 5;
  } else if (step <= 10) {
    step = 10;
  } else {
    step = Math.ceil(roughStep / 10) * 10;
  }

  const ticks = [0];
  for (let v = step; v < upper - 1e-9; v += step) {
    ticks.push(Number(Number(v).toFixed(6)));
  }
  const last = ticks[ticks.length - 1];
  if (last == null || last < upper - 1e-6) {
    ticks.push(Number(upper.toFixed(4)));
  }
  return { ticks, rangeMax: upper };
}

const POP_CUM_RIGHT_TICKS = [0, 20, 40, 60, 80, 100];

/**
 * Aligned ticks for % population (left) vs cumulative % (right) dual-axis pond chart.
 * Grid lines at the same vertical fraction on both axes (e.g. 5 ↔ 20 when leftMax is 25).
 */
export function buildPopulationCumulativeDualAxisTicks(maxPctObserved) {
  const maxPct = Number.isFinite(Number(maxPctObserved))
    ? Math.max(0, Number(maxPctObserved))
    : 0;
  let leftMax = Math.ceil(maxPct / 5) * 5;
  if (leftMax < 5) leftMax = 5;
  if (maxPct > 0 && leftMax < maxPct * 1.05) {
    leftMax += 5;
  }
  const rightTicks = [...POP_CUM_RIGHT_TICKS];
  const leftTicks = rightTicks.map((t) => Number(((t / 100) * leftMax).toFixed(6)));
  return {
    leftTicks,
    leftMax,
    rightTicks,
    rightMax: 100,
  };
}

/**
 * Aligned ticks for WQ dual-axis (e.g. temperature left, mg/L right 0–10 step 1).
 * Both axes share the same number of tick positions so horizontal grid lines match.
 * @param {number} primaryMinObserved
 * @param {number} primaryMaxObserved
 * @param {[number, number]} [secondaryRange]
 * @param {{ secondaryStep?: number, tickCount?: number }} [options]
 */
export function buildWqDualAxisTicks(
  primaryMinObserved,
  primaryMaxObserved,
  secondaryRange = [0, 10],
  options = {}
) {
  const { secondaryStep = 1, tickCount = 10 } = options;
  const [secLo, secHi] = secondaryRange;
  const secSpan = secHi - secLo || 1;

  let secondaryTicks;
  if (
    secondaryStep === 1 &&
    Number.isInteger(secLo) &&
    Number.isInteger(secHi) &&
    tickCount === secHi - secLo + 1
  ) {
    secondaryTicks = Array.from({ length: tickCount }, (_, i) => secLo + i * secondaryStep);
  } else if (secondaryStep === 1 && tickCount === secHi - secLo) {
    secondaryTicks = Array.from({ length: tickCount }, (_, i) => secLo + i * secondaryStep);
  } else if (tickCount >= 2) {
    secondaryTicks = Array.from({ length: tickCount }, (_, i) =>
      Number((secLo + (i * secSpan) / (tickCount - 1)).toFixed(6))
    );
  } else {
    secondaryTicks = [];
    for (let v = secLo; v <= secHi + 1e-9; v += secondaryStep) {
      secondaryTicks.push(Number(v.toFixed(6)));
    }
  }

  let pMin = Number.isFinite(primaryMinObserved)
    ? Math.floor(primaryMinObserved / 5) * 5
    : 40;
  let pMax = Number.isFinite(primaryMaxObserved)
    ? Math.ceil(primaryMaxObserved / 5) * 5
    : 80;
  if (pMax <= pMin) pMax = pMin + 40;
  if (Number.isFinite(primaryMinObserved) && primaryMinObserved < pMin) {
    pMin = Math.floor(primaryMinObserved / 5) * 5;
  }
  if (Number.isFinite(primaryMaxObserved) && primaryMaxObserved > pMax) {
    pMax = Math.ceil(primaryMaxObserved / 5) * 5;
  }

  const primaryTicks = secondaryTicks.map((st) => {
    const frac = (st - secLo) / secSpan;
    return Number((pMin + frac * (pMax - pMin)).toFixed(6));
  });
  const primaryTickText = primaryTicks.map((v) => String(Math.round(v)));

  return {
    primaryTicks,
    primaryTickText,
    primaryMin: pMin,
    primaryMax: pMax,
    secondaryTicks,
    secondaryMin: secLo,
    secondaryMax: secHi,
  };
}
